import type { Command, Node } from "@mapcraft-tasks/client-store";
import { useDispatch } from "@mapcraft-tasks/client-store/react";
import { allowedStatuses, type PlainDate } from "@mapcraft-tasks/domain";
import { useCallback } from "react";
import { playComplete } from "./sound";

/**
 * Галочка: у кого она есть и что значит нажатие.
 *
 * Смысл один на всё приложение, а мест, где по ноде отмечают «сделал», три: строка
 * аутлайна, заголовок страницы и панели справа. Расходятся они только тем, ОТКУДА
 * берут ноду; правило — здесь, одним куском.
 */

/**
 * Спрашиваем не роль перечислением, а матрицу role/status: галочка и есть «по ноде
 * принимается решение». У `plain` и системных ролей статуса нет вовсе — значит и
 * отмечать нечего (domain/statuses). Перечисление по месту однажды уже пропустило
 * роль мимо защиты, поэтому его тут нет.
 */
export function hasCheckbox(node: Node): boolean {
	return allowedStatuses(node.role) !== null;
}

/**
 * Что делает нажатие галочки: команда вперёд, обратная к ней и подпись для отмены.
 * `null` — у ноды галочки нет, трогать нечего.
 *
 * У повторяющейся каждое «сделал» — отдельная строка события, статус не трогается:
 * поэтому её галочку жмут и второй раз, и пятый, пока не набрано x/N. `on` для
 * повторяющейся — дата зачёта: панель справа умеет смотреть не только на сегодня.
 */
export function toggleDoneCommands(
	node: Node,
	on?: PlainDate
): { forward: Command; back: Command; label: string } | null {
	if (!hasCheckbox(node)) {
		return null;
	}
	if (node.role === "recurring") {
		return {
			// `clientEventId` — ключ намерения: ретрай отправки не должен завысить x/N.
			forward: { name: "completeNode", input: { nodeId: node.id, on, clientEventId: crypto.randomUUID() } },
			back: { name: "uncompleteNode", input: { nodeId: node.id, on } },
			label: "выполнение"
		};
	}
	const wasDone = node.status === "done";
	return {
		forward: wasDone
			? { name: "uncompleteNode", input: { nodeId: node.id } }
			: { name: "completeNode", input: { nodeId: node.id } },
		back: wasDone
			? { name: "completeNode", input: { nodeId: node.id } }
			: { name: "uncompleteNode", input: { nodeId: node.id } },
		label: wasDone ? "снятие галочки" : "галочка"
	};
}

/**
 * Нажать галочку вне аутлайна. У заголовка страницы и панелей своего курсора нет,
 * поэтому ноду передают явно; истории отмены у них тоже нет — `Cmd+Z` живёт в
 * аутлайне и правит его выделение, а не то, что нажали в панели.
 *
 * Звук «сделано» здесь свой ровно по той же причине, по какой он в `run` аутлайна:
 * он висит на отправке команды, а не на обработчике, — иначе каждый новый путь
 * пришлось бы озвучивать заново.
 */
export function useToggleDone(): (node: Node, on?: PlainDate) => void {
	const dispatch = useDispatch();
	return useCallback(
		(node: Node, on?: PlainDate) => {
			const step = toggleDoneCommands(node, on);
			if (step === null) {
				return;
			}
			dispatch(step.forward);
			if (step.forward.name === "completeNode") {
				playComplete();
			}
		},
		[dispatch]
	);
}
